import clsx from 'clsx';
import * as React from 'react';

import {
    type BlockTableData,
    type TableColumnDefinition,
    type TableDefinitions,
    type TableView,
    table,
} from '@gitbook/doc-core';
import {
    type ConfirmModalProps,
    createPalette,
    type ModalProps,
    PaletteButton,
    type PaletteDef,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, confirm, toast } from '@gitbook/spine-react';
import type { DeepReadonly } from '@gitbook/util-immutable';

import { capitalizeFirstLetter } from '../../../../../utils';
import { createColumnDefsPalette, definitionSpecs } from '../../ColumnDefinitions';
import { type TableDefinitionViewCommandsParams, viewSpecs } from '../../Views';

interface MoreOptionsButtonProps {
    /** Key of the parent Table block */
    nodeKey: string;

    /** Editor instance */
    editor: EditorContainer;

    /** Current view in the table */
    view: TableView;

    /** Table definition */
    tableDefinition: TableDefinitions;

    /** Column definition of the column */
    columnDefinition: TableColumnDefinition;

    /** Current column is hidden by user */
    isHidden: boolean;
}

/**
 * Button to edit a column definition.
 */
export function MoreOptionsButton(
    props: MoreOptionsButtonProps & Pick<ButtonProps, 'className' | 'leadingIcon' | 'trailingIcon'>
): React.ReactElement {
    const {
        nodeKey,
        editor,
        view: initialView,
        columnDefinition: initialDefinition,
        tableDefinition,
    } = props;

    const [modal, setModal] = React.useState<null | {
        type: 'command';
        modal: React.ReactElement<ModalProps> | React.ReactElement<ConfirmModalProps>;
    }>(null);

    const updateDefinition = (columnDefinition: TableColumnDefinition) => {
        editor.change((change) => {
            table.setColumnDefinition(change, nodeKey, initialDefinition.id, columnDefinition);
        });
    };

    const refresh: () => [
        PaletteDef,
        TableDefinitionViewCommandsParams<TableView, TableColumnDefinition>,
    ] = () => {
        const tableNode = editor.value.document.assertBlock(nodeKey);
        const data = tableNode.data.toFrozenObject<BlockTableData>();
        const definition = data.definition[initialDefinition.id];

        return [
            generatePalette(data.view, definition),
            generateCommandsParams(data.view, definition),
        ];
    };

    const generateCommandsParams = (
        view: DeepReadonly<TableView>,
        definition: DeepReadonly<TableColumnDefinition>
    ): TableDefinitionViewCommandsParams<TableView, TableColumnDefinition> => {
        return {
            definition,
            view,
            setView: (newView) => {
                editor.change((change) => {
                    table.setView(change, nodeKey, newView);
                });

                return refresh();
            },
            setDefinition: (newDefinition: TableColumnDefinition) => {
                updateDefinition(newDefinition);
                return refresh();
            },
            openModal: (CommandModal) => {
                setModal({
                    type: 'command',
                    modal: <CommandModal definition={definition} onClose={() => setModal(null)} />,
                });
            },
        };
    };

    const generatePalette = (
        view: DeepReadonly<TableView>,
        columnDefinition: DeepReadonly<TableColumnDefinition>
    ): PaletteDef => {
        const spec = definitionSpecs[columnDefinition.type];
        const viewSpec = viewSpecs[view.type];
        const params = generateCommandsParams(view, columnDefinition);

        const typeCommands = spec.commands?.(params) ?? [];
        if (typeCommands.length > 0 && !typeCommands[0].title) {
            typeCommands[0] = {
                ...typeCommands[0],
                title: 'Type properties',
            };
        }

        return createPalette({
            groups: [
                ...typeCommands,
                ...(viewSpec.definitionCommands ? viewSpec.definitionCommands(params) : []),
                {
                    key: 'definition.danger',
                    commands: [
                        {
                            key: 'definition.add',
                            title: `Add ${viewSpec.definitionName}`,
                            icon: Icon.AddColumnRight,
                            trigger: () => {
                                editor.change((change) => {
                                    const curColumnIndex = [...view.columns].findIndex(
                                        (value) => columnDefinition.id === value
                                    );
                                    table.insertColumn(
                                        change,
                                        nodeKey,
                                        table.createColumnFromType('text'),
                                        curColumnIndex + 1
                                    );
                                });
                            },
                            descendant: () => {
                                return createColumnDefsPalette((type) => {
                                    const curColumnIndex = [...view.columns].findIndex(
                                        (value) => columnDefinition.id === value
                                    );
                                    editor.change((change) => {
                                        table.insertColumn(
                                            change,
                                            nodeKey,
                                            table.createColumnFromType(type),
                                            curColumnIndex + 1
                                        );
                                    });
                                });
                            },
                        },

                        props.isHidden
                            ? {
                                  key: 'definition.show',
                                  title: `Show ${viewSpec.definitionName}`,
                                  icon: Icon.Eye,
                                  trigger: () => {
                                      void toast.catch(() => {
                                          editor.change((change) => {
                                              const originalIndex = Object.keys(
                                                  tableDefinition
                                              ).indexOf(columnDefinition.id);

                                              table.showColumn({
                                                  change,
                                                  tableKey: nodeKey,
                                                  columnId: columnDefinition.id,
                                                  atIndex: originalIndex,
                                              });
                                          });

                                          toast.success(
                                              `${capitalizeFirstLetter(viewSpec.definitionName)} is now visible in ${view.type}`
                                          );
                                      }, `Error showing ${viewSpec.definitionName}`);
                                  },
                              }
                            : {
                                  key: 'definition.hide',
                                  title: `Hide ${viewSpec.definitionName}`,
                                  icon: Icon.EyeOff,
                                  trigger: () => {
                                      void toast.catch(() => {
                                          editor.change((change) => {
                                              table.hideColumn(
                                                  change,
                                                  nodeKey,
                                                  columnDefinition.id
                                              );
                                          });
                                          toast.success(
                                              `${capitalizeFirstLetter(
                                                  viewSpec.definitionName
                                              )} is hidden from ${view.type}`
                                          );
                                      }, `Error hiding ${viewSpec.definitionName}`);
                                  },
                              },
                        {
                            key: 'definition.delete',
                            title: `Delete ${viewSpec.definitionName}`,
                            icon: Icon.Trashcan,
                            trigger: () => {
                                void confirm({
                                    title: `Delete ${viewSpec.definitionName}`,
                                    description:
                                        'Do you really want to delete this column and all of its data?',
                                    kind: 'danger',
                                    confirmLabel: 'Delete',
                                    onConfirm: async (_event, { preventClose }) => {
                                        await toast.catch(
                                            () => {
                                                editor.change((change) => {
                                                    table.removeColumn(
                                                        change,
                                                        nodeKey,
                                                        initialDefinition.id
                                                    );
                                                });
                                                toast.success(
                                                    `${capitalizeFirstLetter(
                                                        viewSpec.definitionName
                                                    )} deleted`
                                                );
                                            },
                                            `Error deleting ${viewSpec.definitionName}`,
                                            { onReject: preventClose }
                                        );
                                    },
                                });
                            },
                        },
                    ],
                },
            ],
        });
    };

    return (
        <>
            {modal?.type === 'command' ? modal.modal : null}
            <PaletteButton
                trailingIcon={Icon.MoreVertical}
                palette={() => generatePalette(initialView, initialDefinition)}
                kind="blank"
                iconOnly
                label={definitionSpecs[initialDefinition.type].title}
                title="Click for options"
                className={clsx('ml-auto')}
            />
        </>
    );
}
